import { makeStyles } from '@material-ui/styles'
import { listTenants } from 'account/components/userManagement/tenants/new-actions'
import { tenantsSelector } from 'account/components/userManagement/tenants/selectors'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import PicklistField from 'core/components/validatedForm/DropdownField'
import FormFieldSection from 'core/components/validatedForm/FormFieldSection'
import KeyValuesField from 'core/components/validatedForm/KeyValuesField'
import ListTableField from 'core/components/validatedForm/ListTableField'
import TextField from 'core/components/validatedForm/TextField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { Route } from 'core/plugins/route'
import Theme from 'core/themes/model'
import { routeManager } from 'core/utils/routes'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { addVolumeTypeTenant, createVolumeType } from './actions'
import VolumeBackendPicklist from './VolumeBackendPicklist'

const useStyles = makeStyles<Theme>((theme) => ({
  withUnits: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  unitsDropdown: {
    position: 'relative',
    bottom: 1,
  },
}))

const tenantColumns = [
  {
    id: 'name',
    label: 'Name',
  },
  {
    id: 'description',
    label: 'Description',
  },
]

interface Props {
  addRoute: Route
}

export default function CreateVolumeModal({ addRoute }: Props) {
  const { history, location } = useReactRouter()
  const [errorMessage, setErrorMessage] = useState<{ title: string; message: string } | undefined>(
    undefined,
  )
  const defaultParams = {
    name: '',
    description: '',
    volumeBackend: '',
    metadata: [],
    tenants: [],
    public: true,
  }
  const { params, getParamsUpdater, updateParams, setParams } = useParams(defaultParams)

  const { loading: loadingTenants } = useListAction(listTenants)
  const tenantsList = useSelector(tenantsSelector)

  const { update, updating, error, reset } = useUpdateAction(createVolumeType)
  const { update: addTenantFn, reset: resetAddTenant } = useUpdateAction(addVolumeTypeTenant)

  const searchParams = new URLSearchParams(location.search)
  const volumeBackendName = searchParams.get('volumeBackendName')

  // If the volume backend name is provided in the URL, set it in the form
  useEffect(() => {
    if (volumeBackendName) {
      updateParams({ volumeBackend: volumeBackendName })
    }
  }, [volumeBackendName])

  const submitForm = useCallback(async () => {
    if (params.metadata.some((data) => data.key === 'volume_backend_name')) {
      setErrorMessage({
        title: 'Invalid Key-Value Pair',
        message:
          'Cannot add volume_backend_name in Metadata Key-Value pair. Please select the Volume Backend from the dropdown.',
      })
      return
    }
    setErrorMessage(undefined)

    const inititalValue = !!params.volumeBackend
      ? { volume_backend_name: params.volumeBackend }
      : {}

    const metadata = params.metadata.reduce((accum, pair) => {
      return {
        ...accum,
        [pair.key]: pair.value,
      }
    }, inititalValue)

    const body = {
      volume_type: {
        name: params.name,
        description: params.description,
        extra_specs: metadata,
        'os-volume-type-access:is_public': params.public,
      },
    }

    const { success, response } = await update({ body })
    if (!success) {
      return
    }
    const volumeTypeId = response.id
    if (!params.public) {
      const promises = params.tenants.map((tenant) => {
        const tenantBody = {
          addProjectAccess: {
            project: tenant.id,
          },
        }
        return addTenantFn({ id: volumeTypeId, body: tenantBody })
      })
      await Promise.allSettled(promises)
    }
    if (success) handleClose()
  }, [params])

  const handleClose = () => {
    setParams(defaultParams)
    reset()
    resetAddTenant()
    history.push(routeManager.routes.openstack.volumeTypes.path())
  }

  return (
    <ModalForm
      route={addRoute}
      title={`Create New Volume Type`}
      onSubmit={submitForm}
      onClose={handleClose}
      submitting={updating}
      error={error || errorMessage}
      submitTitle={`Create Volume Type`}
      maxWidth={600}
    >
      <FormFieldSection title="Volume Type Configuration">
        <TextField
          id="name"
          label="Name"
          onChange={getParamsUpdater('name')}
          value={params.name}
          required
        />
        <TextField
          id="description"
          label="Description"
          onChange={getParamsUpdater('description')}
          value={params.description}
          required
        />
        <PicklistField
          DropdownComponent={VolumeBackendPicklist}
          id="volumeType"
          onChange={getParamsUpdater('volumeBackend')}
          value={params.volumeBackend}
          label="Volume Backend"
          includeNoneOption
        />
        <KeyValuesField
          id="metadata"
          label="Metadata"
          value={params.metadata}
          onChange={getParamsUpdater('metadata')}
          addLabel="Add Metadata"
          allowMultipleValues
        />
      </FormFieldSection>
      <FormFieldSection title="Tenant Access">
        <CheckboxField
          id="public"
          label="Make Public"
          value={params.public}
          onChange={getParamsUpdater('public')}
          info="If checked, volume type will be available for every tenant."
        />
        {!params.public && (
          <>
            <Text variant="body2">
              Select the tenants that should have access to this volume type.
            </Text>
            <ListTableField
              id="tenants"
              data={tenantsList}
              loading={loadingTenants}
              columns={tenantColumns}
              onChange={getParamsUpdater('tenants')}
              value={params.tenants}
              uniqueIdentifier="id"
              searchTargets={['name']}
              multiSelection
            />
          </>
        )}
      </FormFieldSection>
    </ModalForm>
  )
}
